// Auth service — thin wrapper around Supabase auth.
// UI still uses placeholder data in V1; these helpers are the seam for real auth.
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/db/models";
import { trackEvent, identifyUser } from "@/lib/analytics";

export interface SignUpInput {
  email: string;
  password: string;
  fullName?: string;
}

export const authService = {
  async signUpCustomer({ email, password, fullName }: SignUpInput) {
    void trackEvent({ event: "signup_started", properties: { method: "password" } });
    // Customers get the 'customer' role automatically via handle_new_user trigger.
    const result = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { full_name: fullName ?? null },
      },
    });
    if (!result.error && result.data.user?.id) {
      void trackEvent({
        event: "signup_completed",
        userId: result.data.user.id,
        properties: { method: "password" },
      });
      void identifyUser(result.data.user.id, { signup_method: "password" });
    }
    return result;
  },

  async signInWithPassword(email: string, password: string) {
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (!result.error && result.data.user?.id) {
      void trackEvent({
        event: "login_completed",
        userId: result.data.user.id,
        properties: { method: "password" },
      });
      void identifyUser(result.data.user.id, { last_login_method: "password" });
    }
    return result;
  },

  async signInStaff(email: string, password: string) {
    // Staff/admin auth uses the same endpoint; role is enforced by DB policies.
    // TODO(live): after sign-in, verify has_role(userId,'staff'|'admin') and
    // sign back out if the account lacks a staff/admin role.
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (!result.error && result.data.user?.id) {
      void trackEvent({
        event: "login_completed",
        userId: result.data.user.id,
        properties: { method: "password", scope: "staff" },
      });
    }
    return result;
  },

  async signOut() {
    const { data } = await supabase.auth.getSession();
    const userId = data.session?.user?.id;
    const result = await supabase.auth.signOut();
    void trackEvent({ event: "logout_completed", userId });
    return result;
  },

  async getSession() {
    return supabase.auth.getSession();
  },

  async getUser() {
    return supabase.auth.getUser();
  },

  onAuthStateChange(cb: Parameters<typeof supabase.auth.onAuthStateChange>[0]) {
    return supabase.auth.onAuthStateChange(cb);
  },

  async getRoles(userId: string): Promise<AppRole[]> {
    const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", userId);
    if (error) throw error;
    return (data ?? []).map((r) => r.role as AppRole);
  },

  async hasRole(userId: string, role: AppRole): Promise<boolean> {
    const roles = await this.getRoles(userId);
    return roles.includes(role);
  },

  /** Route to send a user to after login based on their highest role. */
  landingPathForRoles(roles: AppRole[]): string {
    if (roles.includes("admin") || roles.includes("staff")) return "/operations";
    return "/dashboard";
  },
};
